import { useMutation } from 'react-query'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { sendToApi } from 'api/client'
import { Archetype, VisualGenerateRequest } from 'types'
import { buildErrorMessage } from 'utils'
import { UseMutationOptions } from 'react-query/types/react/types'
import { CrossPostAccount } from 'components/Wizards/Wizard/Form/CrossPostAccountsInput/CrossPostAccountsInput'

interface Props {
  workspaceId: number
}

export interface Data {
  request: VisualGenerateRequest
}

export interface Variables {
  title: string
  parent_id: string
  emoji?: string
  width: number
  height: number
  channel: string
  brand_visual_kit_id?: number
  source_visual_element_id?: number
  completed_actions: Record<string, boolean>
  selected_caption_id?: number
  captions?: Array<string>
  topic?: string
  brand_voice_id?: number
  tone?: Array<string>
  reference_text?: string
  language?: string
  external_integration_id?: number
  cross_post_accounts: Array<CrossPostAccount>
  initial_media_file_id?: string
  initial_media_file_remove_background?: boolean
  brand_kit_id?: number
  content?: object
  archetype?: Archetype
  links?: string[]
  seed: {
    apply_brand: boolean
  }
}

export type Options = Omit<UseMutationOptions<Data, unknown, Variables>, 'mutationKey' | 'mutationFn'>

const generateDesign = async (workspaceId: number, variables: Variables) => {
  const response = await sendToApi<Data>(`/w/${workspaceId}/designs/generate`, {
    method: 'POST',
    body: {
      parent_id: variables.parent_id,
      emoji: variables.emoji,
      completed_actions: variables.completed_actions,
      selected_caption_id: variables.selected_caption_id,
      captions: variables.captions,
      content: variables.content,
      prompt: {
        topic: variables.topic,
        brand_voice_id: variables.brand_voice_id,
        tone: variables.tone,
        reference_text: variables.reference_text,
        language: variables.language,
        external_integration_id: variables.external_integration_id,
        generation_type: variables.channel,
        title: variables.title,
        source_visual_element_id: variables.source_visual_element_id,
        apply_brand: variables.seed.apply_brand,
        brand_visual_kit_id: variables.brand_visual_kit_id,
        width: variables.width,
        height: variables.height,
        cross_post_accounts: variables.cross_post_accounts,
        initial_media_file_id: variables.initial_media_file_id,
        initial_media_file_remove_background: variables.initial_media_file_remove_background,
        brand_kit_id: variables.brand_kit_id,
        archetype: variables.archetype,
        ...(variables.links?.length ? { links: variables.links } : {}),
      },
    },
  })

  if (response.type !== SUCCESS_RESPONSE_TYPE) {
    throw new Error(
      buildErrorMessage(response.data.errors) ||
        'An error occurred! Our team has been notified and we are looking into it.',
    )
  }
  return response.data
}

export const useGenerateDesign = ({ workspaceId }: Props, options?: Options) => {
  return useMutation<Data, unknown, Variables>(
    'generateDesign',
    async (variables) => generateDesign(workspaceId, variables),
    options,
  )
}
